import type { Home } from '@lmc/ts-common'
import {
  FilterContainer,
  GroupedResidentsMultiSelect,
  GroupedUsersMultiSelect,
  MultiSelect,
  RangePicker,
  Select
} from '@lmc/ui-kit'
import type { SelectVariant } from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { Text } from '@lmc/ui-kit/src/general'
import type { Theme } from '@lmc/ui-kit/src/styled'
import type { DatePickerProps, SelectProps } from 'antd'
import type dayjs from 'dayjs'
import { useGroupedResidents } from 'hooks/useGroupedOptions'
import useResidentsList from 'hooks/useResidentsList'
import useUsersList from 'hooks/useUser/useUsersList'
import { NAMESPACE } from 'locale'
import { monitoringReportConstants as translations } from 'locale/monitoringReports'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import type { RangeValue } from 'types/shared'

export type BasicFiltersValue = {
  rangePicker?: RangeValue<dayjs.Dayjs>
  residents?: string[]
  loggedBy?: string[]
  resident?: string
}

export type DatePickerType = DatePickerProps['picker']

export type BasicFiltersProps<FiltersValue extends BasicFiltersValue> = {
  value: FiltersValue
  onChange: (value: FiltersValue) => void
  withLoggedBy?: boolean
  hasResidentsFilter?: boolean
  hasRangePicker?: boolean
  allowClearDateRange?: boolean
  variant?: SelectVariant
  hasSingleResident?: boolean
  picker?: DatePickerType
  testId?: string
}

export const getName = (item: { name: { first: string; last: string } }) =>
  `${item.name.first} ${item.name.last}`

const BasicFilters = ({
  value,
  onChange,
  children,
  withLoggedBy = true,
  hasResidentsFilter = true,
  allowClearDateRange = true,
  hasRangePicker = true,
  variant = 'default',
  hasSingleResident = false,
  picker,
  testId = 'basic-filters'
}: BasicFiltersProps<BasicFiltersValue> & {
  children?: ReactNode
}) => {
  const { t } = useTranslation(NAMESPACE.MONITORING_REPORTS)

  const handleInputChange = (newValue: Partial<BasicFiltersValue>) => {
    const updatedValue: BasicFiltersValue = {
      ...value,
      ...newValue
    }

    onChange(updatedValue)
  }
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { residents } = useResidentsList({})
  const { groupedResidentOptions } = useGroupedResidents(residents)

  const { users } = useUsersList({})

  // we agreed that we're not using render functions except for special cases.
  // special case here, didn't want to duplicate code and a new component does not make sense here
  const renderFilters = () => (
    <>
      {hasRangePicker && (
        <StyledRangePicker
          data-testid={`${testId}-range-picker`}
          value={value.rangePicker}
          onChange={(rangePicker) => handleInputChange({ rangePicker })}
          placeholder={[t(translations.FROM), t(translations.TO)]}
          allowClear={allowClearDateRange}
          $variant={variant}
          picker={picker}
          format={picker === 'month' ? 'MM/YYYY' : undefined}
        />
      )}
      {hasResidentsFilter && (
        <GroupedResidentsMultiSelect
          data-testid={`${testId}-residents-select`}
          residents={residents}
          title={preferredPeopleNominative || ''}
          values={value.residents}
          handleInputChange={(selectedResidents) =>
            handleInputChange({ residents: selectedResidents })
          }
          variant={variant}
        />
      )}
      {hasSingleResident && (
        <StyledSingleSelect
          data-testid={`${testId}-resident-select`}
          value={value.resident}
          placeholder={
            <StyledPlaceholder level={1} $variant={variant}>
              {preferredPeopleNominative}
            </StyledPlaceholder>
          }
          options={groupedResidentOptions}
          onChange={(selectedResidents) =>
            handleInputChange({ resident: selectedResidents })
          }
          $variant={variant}
          allowClear
        />
      )}
      {withLoggedBy && (
        <GroupedUsersMultiSelect
          data-testid={`${testId}-logged-by-select`}
          users={users}
          title={t(translations.LOGGED_BY)}
          values={value?.loggedBy}
          handleInputChange={(selectedOptions) =>
            handleInputChange({
              loggedBy: selectedOptions as string[]
            })
          }
        />
      )}
      {children}
    </>
  )

  return (
    <FilterContainer title={t(translations.FILTERS)} data-testid={testId}>
      {renderFilters()}
    </FilterContainer>
  )
}

export const StyledMultiSelect = styled(MultiSelect)<{ $variant: string }>`
  min-width: 200px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
` as typeof MultiSelect

interface CustomSelectProps extends SelectProps<any> {
  $variant: string
}

const highContrastStyles = (theme: Theme) => css`
  .ant-select-selector {
    border-color: ${theme.color.neutral.color5} !important;
  }

  .ant-select-arrow {
    color: ${theme.color.neutral.color5} !important;
  }
`

const StyledSingleSelect = styled(Select)<CustomSelectProps>`
  min-width: 200px;

  ${({ $variant, theme }) =>
    $variant === 'highContrast' && highContrastStyles(theme)}
`

const StyledRangePicker = styled(RangePicker)<{ $variant: string }>`
  width: 200px;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    width: 65%;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }

  ${({ $variant, theme }) =>
    $variant === 'highContrast' &&
    css`
      border-color: ${theme.color.neutral.color5} !important;
      .ant-picker-suffix {
        color: ${theme.color.neutral.color5} !important;
      }
    `}
`

const StyledPlaceholder = styled(Text)<{ $variant: string }>`
  color: ${(props) =>
    props.$variant === 'highContrast'
      ? props.theme.color.neutral.color3
      : 'inherit'};
`

export default BasicFilters
